import { createClient } from "@supabase/supabase-js";
import { GENDER, CURRENT_SEASON } from "../constants";
import { normalizeToBR } from "./timezone";

// ─── SUPABASE CONFIG ──────────────────────────────────────────────────────────
const SUPABASE_URL = "https://mcyiufzzwcznaspfdfoy.supabase.co";
const SUPABASE_KEY =
  "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6Im1jeWl1Znp6d2N6bmFzcGZkZm95Iiwicm9sZSI6ImFub24iLCJpYXQiOjE3NzI2NDA5NDAsImV4cCI6MjA4ODIxNjk0MH0.ITRG4U7TqPH3UYh06K1UdRi7LUwqVH5Gab6Lq7WmjKM";

export const supabase = createClient(SUPABASE_URL, SUPABASE_KEY);

// ─── TABLE RESOLVER ───────────────────────────────────────────────────────────
function t(gender, key) {
  return GENDER.tables[gender]?.[key] ?? GENDER.tables.M[key];
}

// ─── MAPPERS ──────────────────────────────────────────────────────────────────
const mapPlayer = (p) => ({
  id: p.id,
  name: p.name,
  position: p.position,
  initialPosition: p.initial_position ?? null,
  password: p.password,
  blockedAsChallenger: p.blocked_as_challenger,
  blockedAsChallenged: p.blocked_as_challenged,
  inactiveUntil: p.inactive_until,
  inactiveSince: p.inactive_since || null,
  photo: p.photo || null,
  positionDelta: p.position_delta || null,
  positionDeltaDate: p.position_delta_date || null,
  email: p.email || "",
  phone: p.phone || "",
});

// Mapper leve: sem photo — usado no re-fetch do Realtime.
// A foto é preservada do estado anterior (já em cache no React).
const mapPlayerLight = (p) => ({
  id: p.id,
  name: p.name,
  position: p.position,
  initialPosition: p.initial_position ?? null,
  password: p.password,
  blockedAsChallenger: p.blocked_as_challenger,
  blockedAsChallenged: p.blocked_as_challenged,
  inactiveUntil: p.inactive_until,
  inactiveSince: p.inactive_since || null,
  positionDelta: p.position_delta || null,
  positionDeltaDate: p.position_delta_date || null,
  email: p.email || "",
  phone: p.phone || "",
});

const mapChallenge = (c) => ({
  id: c.id,
  challengerId: c.challenger_id,
  challengedId: c.challenged_id,
  challengerName: c.challenger_name,
  challengedName: c.challenged_name,
  dates: c.proposed_dates || [],
  confirmedDate: normalizeToBR(c.confirmed_date),
  status: c.status,
  proposedAt: c.proposed_at,
  adminCreated: c.admin_created || false,
  lastProposedBy: c.last_proposed_by || "challenger",
});

const mapResult = (r) => ({
  id: r.id,
  challengeId: r.challenge_id,
  challengerId: r.challenger_id || null,
  challengedId: r.challenged_id || null,
  challengerName: r.challenger_name,
  challengedName: r.challenged_name,
  winner: r.winner,
  type: r.type,
  score: r.score,
  date: r.date,
});

const mapNotif = (n) => ({
  id: n.id,
  playerId: n.player_id,
  text: n.text,
  date: n.date,
  read: n.read,
  gender: n.gender,
});

const mapHoliday = (h) => ({
  id: h.id,
  date: h.date,
  name: h.name,
  isFixed: h.is_fixed || false,
});

const mapCourtSlot = (s) => ({
  id: s.id,
  dayType: s.day_type,
  hour: s.hour,
  maxCourts: s.max_courts,
});

const mapBlockedPeriod = (p) => ({
  id: p.id,
  dateFrom: p.date_from,
  dateTo: p.date_to,
  allDay: p.all_day,
  blockedHours: p.blocked_hours || [],
  label: p.label || "",
});

// ─── DB FUNCTIONS ─────────────────────────────────────────────────────────────

// Carrega estado completo — usado no boot e na troca de gênero.
// Inclui photo (URL curta após migração para o Storage).
export async function dbLoadState(gender = "M") {
  const otherGenders = Object.keys(GENDER.tables).filter((g) => g !== gender);
  const [
    { data: players },
    { data: challenges },
    { data: results },
    { data: notifications },
    { data: rulesData },
    { data: blockedDates },
    otherChallengesRes,
    { data: holidays },
    { data: courtSlots },
    { data: blockedPeriods },
  ] = await Promise.all([
    supabase.from(t(gender, "players")).select("*").order("position"),
    supabase.from(t(gender, "challenges")).select("*").order("created_at"),
    supabase.from(t(gender, "results")).select("*").eq("season", CURRENT_SEASON).order("date"),
    supabase.from("notifications").select("*").eq("gender", gender).order("date", { ascending: false }),
    supabase.from("rules").select("*").order("id"),
    supabase.from("blocked_dates").select("*").order("date"),
    Promise.all(otherGenders.map((g) => supabase.from(t(g, "challenges")).select("*").order("created_at"))),
    supabase.from("holidays").select("*").order("date"),
    supabase.from("court_slots").select("*").order("day_type").order("hour"),
    supabase.from("blocked_periods").select("*").order("date_from"),
  ]);

  const mappedChallenges = (challenges || []).map(mapChallenge);
  // Desafios das outras divisões (as quadras são compartilhadas entre todas)
  const mappedOther = (otherChallengesRes || []).flatMap((r) => (r.data || []).map(mapChallenge));

  return {
    players: (players || []).map(mapPlayer),
    challenges: mappedChallenges,
    allChallenges: [...mappedChallenges, ...mappedOther],
    results: (results || []).map(mapResult),
    notifications: (notifications || []).map(mapNotif),
    rules: rulesData && rulesData.length > 0 ? rulesData : null,
    blockedDates: (blockedDates || []).map((b) => ({
      id: b.id,
      date: b.date,
      allDay: b.all_day,
      blockedHours: b.blocked_hours || [],
    })),
    holidays: (holidays || []).map(mapHoliday),
    courtSlots: (courtSlots || []).map(mapCourtSlot),
    blockedPeriods: (blockedPeriods || []).map(mapBlockedPeriod),
    nextChallengeId: 1,
  };
}

// Carrega estado leve — usado exclusivamente no re-fetch do Realtime.
// NÃO busca a coluna photo: as URLs do Storage são imutáveis e já estão
// em cache no estado React — não precisam ser rebuscadas a cada evento.
// Busca results para refletir novos resultados registrados pelo admin em tempo real.
// Isso reduz o egress do re-fetch em ~90% vs o dbLoadState completo.
export async function dbLoadStateLite(gender = "M", prevState = null) {
  const otherGenders = Object.keys(GENDER.tables).filter((g) => g !== gender);
  const [
    { data: players },
    { data: challenges },
    { data: notifications },
    otherChallengesRes,
    { data: holidays },
    { data: blockedDates },
    { data: results },
    { data: courtSlots },
    { data: blockedPeriods },
  ] = await Promise.all([
    supabase
      .from(t(gender, "players"))
      .select("id, name, position, initial_position, password, blocked_as_challenger, blocked_as_challenged, inactive_until, inactive_since, position_delta, position_delta_date, email, phone")
      .order("position"),
    supabase.from(t(gender, "challenges")).select("*").order("created_at"),
    supabase.from("notifications").select("*").eq("gender", gender).order("date", { ascending: false }),
    Promise.all(otherGenders.map((g) => supabase.from(t(g, "challenges")).select("*").order("created_at"))),
    supabase.from("holidays").select("*").order("date"),
    supabase.from("blocked_dates").select("*").order("date"),
    supabase.from(t(gender, "results")).select("*").eq("season", CURRENT_SEASON).order("date"),
    supabase.from("court_slots").select("*").order("day_type").order("hour"),
    supabase.from("blocked_periods").select("*").order("date_from"),
  ]);

  const mappedChallenges = (challenges || []).map(mapChallenge);
  // Desafios das outras divisões (as quadras são compartilhadas entre todas)
  const mappedOther = (otherChallengesRes || []).flatMap((r) => (r.data || []).map(mapChallenge));

  // Preserva fotos do estado anterior (cache local)
  const prevPhotos = {};
  if (prevState?.players) {
    prevState.players.forEach((p) => { if (p.photo) prevPhotos[p.id] = p.photo; });
  }

  const mergedPlayers = (players || []).map((p) => ({
    ...mapPlayerLight(p),
    photo: prevPhotos[p.id] ?? null,
  }));

  return {
    ...(prevState || {}), // mantém rules
    players: mergedPlayers,
    challenges: mappedChallenges,
    allChallenges: [...mappedChallenges, ...mappedOther],
    notifications: (notifications || []).map(mapNotif),
    holidays: (holidays || []).map(mapHoliday),
    blockedDates: (blockedDates || []).map((b) => ({
      id: b.id,
      date: b.date,
      allDay: b.all_day,
      blockedHours: b.blocked_hours || [],
    })),
    results: (results || []).map(mapResult),
    courtSlots: (courtSlots || []).map(mapCourtSlot),
    blockedPeriods: (blockedPeriods || []).map(mapBlockedPeriod),
  };
}

export async function dbSavePlayers(players, gender = "M") {
  const rows = players.map((p) => ({
    id: p.id,
    name: p.name,
    position: p.position,
    initial_position: p.initialPosition ?? null,
    password: p.password,
    blocked_as_challenger: (p.blockedAsChallenger && new Date(p.blockedAsChallenger) > new Date()) ? p.blockedAsChallenger : null,
    blocked_as_challenged: (p.blockedAsChallenged && new Date(p.blockedAsChallenged) > new Date()) ? p.blockedAsChallenged : null,
    inactive_until: p.inactiveUntil || null,
    inactive_since: p.inactiveSince || null,
    photo: p.photo || null,
    position_delta: p.positionDelta || null,
    position_delta_date: p.positionDeltaDate || null,
    email: p.email || null,
    phone: p.phone || null,
  }));
  const { error } = await supabase
    .from(t(gender, "players"))
    .upsert(rows, { onConflict: "id" });
  if (error) throw error;
}

export async function dbInsertChallenge(c, gender = "M") {
  const { data, error } = await supabase
    .from(t(gender, "challenges"))
    .insert({
      challenger_id: c.challengerId,
      challenged_id: c.challengedId,
      challenger_name: c.challengerName,
      challenged_name: c.challengedName,
      proposed_dates: c.dates,
      confirmed_date: c.confirmedDate || null,
      status: c.status,
      proposed_at: c.proposedAt,
      admin_created: c.adminCreated || false,
      last_proposed_by: c.lastProposedBy || "challenger",
    })
    .select()
    .single();
  if (error) throw error;
  return data.id;
}

export async function dbUpdateChallenge(id, fields, gender = "M") {
  const mapped = {};
  if (fields.status !== undefined) mapped.status = fields.status;
  if (fields.confirmedDate !== undefined) mapped.confirmed_date = fields.confirmedDate;
  if (fields.proposed_dates !== undefined) mapped.proposed_dates = fields.proposed_dates;
  if (fields.proposedAt !== undefined) mapped.proposed_at = fields.proposedAt;
  if (fields.lastProposedBy !== undefined) mapped.last_proposed_by = fields.lastProposedBy;
  const { error } = await supabase.from(t(gender, "challenges")).update(mapped).eq("id", id);
  if (error) throw error;
}

export async function dbDeleteChallenge(id, gender = "M") {
  const { error } = await supabase.from(t(gender, "challenges")).delete().eq("id", id);
  if (error) throw error;
}

export async function dbInsertResult(r, gender = "M") {
  const { error } = await supabase.from(t(gender, "results")).insert({
    challenge_id: r.challengeId || null,
    challenger_id: r.challengerId || null,
    challenged_id: r.challengedId || null,
    challenger_name: r.challengerName,
    challenged_name: r.challengedName,
    winner: r.winner || null,
    type: r.type,
    score: r.score || null,
    date: r.date,
    season: CURRENT_SEASON,
  });
  if (error) throw error;
}

// ─── PHOTO UPLOAD — Supabase Storage ─────────────────────────────────────────
//
// Substitui o antigo sistema de base64 na coluna photo.
// Recebe o base64 já comprimido (gerado no useAuth), faz upload para o
// bucket "avatars" e retorna a URL pública com cache-busting.
// O chamador salva a URL no player via dbSavePlayers.

export async function dbUploadPhoto(playerId, base64DataUrl, tableName = "players") {
  const match = base64DataUrl.match(/^data:([a-zA-Z0-9+/]+\/[a-zA-Z0-9+/]+);base64,(.+)$/);
  if (!match) throw new Error("Formato de imagem inválido");

  const mimeType = match[1];
  const ext = mimeType.includes("png") ? "png" : mimeType.includes("webp") ? "webp" : "jpg";
  const fileName = `${tableName}_${playerId}.${ext}`;

  // Converte base64 → Blob
  const byteString = atob(match[2]);
  const byteArray = new Uint8Array(byteString.length);
  for (let i = 0; i < byteString.length; i++) {
    byteArray[i] = byteString.charCodeAt(i);
  }
  const blob = new Blob([byteArray], { type: mimeType });

  const { error: uploadError } = await supabase.storage
    .from("avatars")
    .upload(fileName, blob, { contentType: mimeType, upsert: true });

  if (uploadError) throw uploadError;

  // Cache-busting via timestamp para forçar reload no browser após troca de foto
  const { data } = supabase.storage.from("avatars").getPublicUrl(fileName);
  return `${data.publicUrl}?t=${Date.now()}`;
}

// ─── NOTIFICATIONS ────────────────────────────────────────────────────────────

export async function dbInsertNotifications(notifs, gender = "M") {
  if (!notifs.length) return;
  const { error } = await supabase.from("notifications").insert(
    notifs.map((n) => ({
      player_id: n.playerId,
      text: n.text,
      date: n.date,
      read: n.read,
      gender,
    }))
  );
  if (error) throw error;
}

export async function dbMarkNotifsRead(playerId, gender = "M") {
  const { error } = await supabase
    .from("notifications")
    .update({ read: true })
    .eq("player_id", playerId)
    .eq("gender", gender)
    .eq("read", false);
  if (error) console.warn("notif read error:", error);
}

export async function dbDeletePlayerNotifs(playerId, gender = "M") {
  const { error } = await supabase
    .from("notifications")
    .delete()
    .eq("player_id", playerId)
    .eq("gender", gender);
  if (error) console.warn("notif delete error:", error);
}

// ─── BLOCKED DATES ────────────────────────────────────────────────────────────

export async function dbInsertBlockedDate(b) {
  const { data, error } = await supabase
    .from("blocked_dates")
    .insert({
      date: b.date,
      all_day: b.allDay,
      blocked_hours: b.allDay ? null : b.blockedHours,
    })
    .select()
    .single();
  if (error) throw error;
  return data.id;
}

export async function dbDeleteBlockedDate(id) {
  const { error } = await supabase.from("blocked_dates").delete().eq("id", id);
  if (error) throw error;
}

// ─── POSITION HISTORY ─────────────────────────────────────────────────────────

const posHistoryTable = (gender) => t(gender, "positionHistory");

export async function dbInsertPositionHistory(entries, gender = "M") {
  if (!entries || !entries.length) return;
  const { error } = await supabase.from(posHistoryTable(gender)).insert(
    entries.map((e) => ({
      player_id: e.playerId,
      player_name: e.playerName,
      position: e.position,
      reason: e.reason,
      detail: e.detail || null,
      date: e.date,
    }))
  );
  if (error) console.warn("position_history insert error:", error);
}

export async function dbLoadPositionHistory(playerId, gender = "M") {
  const { data, error } = await supabase
    .from(posHistoryTable(gender))
    .select("*")
    .eq("player_id", playerId)
    .order("date", { ascending: true });
  if (error) { console.warn("position_history load error:", error); return []; }
  return (data || []).map((e) => ({
    id: e.id,
    playerId: e.player_id,
    playerName: e.player_name,
    position: e.position,
    reason: e.reason,
    detail: e.detail,
    date: e.date,
  }));
}

// ─── HOLIDAYS ─────────────────────────────────────────────────────────────────

export async function dbLoadHolidays() {
  const { data, error } = await supabase
    .from("holidays")
    .select("*")
    .order("date");
  if (error) { console.warn("holidays load error:", error); return []; }
  return (data || []).map(mapHoliday);
}

export async function dbAddHoliday({ date, name }) {
  const { data, error } = await supabase
    .from("holidays")
    .insert({ date, name, is_fixed: false })
    .select()
    .single();
  if (error) throw error;
  return mapHoliday(data);
}

export async function dbDeleteHoliday(id) {
  const { error } = await supabase.from("holidays").delete().eq("id", id);
  if (error) throw error;
}

// ─── COURT SLOTS ──────────────────────────────────────────────────────────────

export async function dbLoadCourtSlots() {
  const { data, error } = await supabase.from("court_slots").select("*").order("day_type").order("hour");
  if (error) { console.warn("court_slots load error:", error); return []; }
  return (data || []).map(mapCourtSlot);
}

export async function dbUpsertCourtSlot({ id, dayType, hour, maxCourts }) {
  if (id) {
    const { error } = await supabase.from("court_slots").update({ hour, max_courts: maxCourts }).eq("id", id);
    if (error) throw error;
  } else {
    const { data, error } = await supabase.from("court_slots").insert({ day_type: dayType, hour, max_courts: maxCourts }).select().single();
    if (error) throw error;
    return data.id;
  }
}

export async function dbDeleteCourtSlot(id) {
  const { error } = await supabase.from("court_slots").delete().eq("id", id);
  if (error) throw error;
}

// ─── BLOCKED PERIODS ──────────────────────────────────────────────────────────

export async function dbInsertBlockedPeriod({ dateFrom, dateTo, allDay, blockedHours, label }) {
  const { data, error } = await supabase.from("blocked_periods").insert({
    date_from: dateFrom,
    date_to: dateTo,
    all_day: allDay,
    blocked_hours: allDay ? null : blockedHours,
    label: label || null,
  }).select().single();
  if (error) throw error;
  return data.id;
}

export async function dbDeleteBlockedPeriod(id) {
  const { error } = await supabase.from("blocked_periods").delete().eq("id", id);
  if (error) throw error;
}

// ─── REALTIME SUBSCRIPTIONS ───────────────────────────────────────────────────

export function dbSubscribe(gender, onRefresh) {
  // Debounce de 1500ms: absorve rajadas de eventos geradas pelo upsert
  // (ex: salvar 100 jogadores gera 100 eventos — o debounce colapsa em 1 re-fetch)
  let debounceTimer = null;
  const trigger = () => {
    clearTimeout(debounceTimer);
    debounceTimer = setTimeout(onRefresh, 1500);
  };

  const playersTable    = t(gender, "players");
  const challengesTable = t(gender, "challenges");
  const resultsTable    = t(gender, "results");

  const chPlayers = supabase
    .channel(`rt-players-${gender}`)
    .on("postgres_changes", { event: "*", schema: "public", table: playersTable }, trigger)
    .subscribe();

  const chChallenges = supabase
    .channel(`rt-challenges-${gender}`)
    .on("postgres_changes", { event: "*", schema: "public", table: challengesTable }, trigger)
    .subscribe();

  const chResults = supabase
    .channel(`rt-results-${gender}`)
    .on("postgres_changes", { event: "*", schema: "public", table: resultsTable }, trigger)
    .subscribe();

  const chNotifs = supabase
    .channel(`rt-notifications-${gender}`)
    .on("postgres_changes", { event: "*", schema: "public", table: "notifications", filter: `gender=eq.${gender}` }, trigger)
    .subscribe();

  const chHolidays = supabase
    .channel(`rt-holidays-${gender}`)
    .on("postgres_changes", { event: "*", schema: "public", table: "holidays" }, trigger)
    .subscribe();

  const chBlockedDates = supabase
    .channel(`rt-blocked-dates-${gender}`)
    .on("postgres_changes", { event: "*", schema: "public", table: "blocked_dates" }, trigger)
    .subscribe();

  const chCourtSlots = supabase
    .channel(`rt-court-slots-${gender}`)
    .on("postgres_changes", { event: "*", schema: "public", table: "court_slots" }, trigger)
    .subscribe();

  const chBlockedPeriods = supabase
    .channel(`rt-blocked-periods-${gender}`)
    .on("postgres_changes", { event: "*", schema: "public", table: "blocked_periods" }, trigger)
    .subscribe();

  return [chPlayers, chChallenges, chResults, chNotifs, chHolidays, chBlockedDates, chCourtSlots, chBlockedPeriods];
}

export async function dbUnsubscribe(channels) {
  if (!channels || !channels.length) return;
  await Promise.all(channels.map((ch) => supabase.removeChannel(ch)));
}

// ─── INACTIVITY HISTORY ───────────────────────────────────────────────────────

const inactHistTable = (gender) => t(gender, "inactivityHistory");

const mapInactHist = (r) => ({
  id: r.id,
  playerId: r.player_id,
  playerName: r.player_name,
  since: r.since,       // "YYYY-MM-DD"
  until: r.until,       // "YYYY-MM-DD"
  days: r.days,
  ytdTotal: r.ytd_total,
  penalty: r.penalty,
  faixa: r.faixa,
  createdAt: r.created_at,
  season: r.season,
});

export async function dbInsertInactivityHistory(entry, gender = "M") {
  const { error } = await supabase.from(inactHistTable(gender)).insert({
    player_id:   entry.playerId,
    player_name: entry.playerName,
    since:       entry.since,
    until:       entry.until,
    days:        entry.days,
    ytd_total:   entry.ytdTotal,
    penalty:     entry.penalty,
    faixa:       entry.faixa,
    season:      CURRENT_SEASON,
  });
  if (error) throw error;
}

export async function dbLoadInactivityHistory(playerId, gender = "M") {
  const { data, error } = await supabase
    .from(inactHistTable(gender))
    .select("*")
    .eq("season", CURRENT_SEASON)
    .eq("player_id", playerId)
    .order("since", { ascending: false });
  if (error) { console.warn("inactivity_history load error:", error); return []; }
  return (data || []).map(mapInactHist);
}

export async function dbLoadAllInactivityHistory(gender = "M") {
  const { data, error } = await supabase
    .from(inactHistTable(gender))
    .select("*")
    .eq("season", CURRENT_SEASON)
    .order("since", { ascending: false });
  if (error) { console.warn("inactivity_history load all error:", error); return []; }
  return (data || []).map(mapInactHist);
}

export async function dbDeleteInactivityHistory(id, gender = "M") {
  const { error } = await supabase.from(inactHistTable(gender)).delete().eq("id", id);
  if (error) throw error;
}

export async function dbUpdateInactivityHistory(id, fields, gender = "M") {
  const { error } = await supabase.from(inactHistTable(gender)).update({
    since:     fields.since,
    until:     fields.until,
    days:      fields.days,
    ytd_total: fields.ytdTotal,
    penalty:   fields.penalty,
    faixa:     fields.faixa,
  }).eq("id", id);
  if (error) throw error;
}

// ─── TEMPORADAS ANTERIORES ────────────────────────────────────────────────────

/** Classificação final de uma temporada arquivada (foto gravada na virada). */
export async function dbLoadSeasonRanking(season, gender = "M") {
  const { data, error } = await supabase
    .from("season_rankings")
    .select("*")
    .eq("season", season)
    .eq("division", gender)
    .order("position", { ascending: true });
  if (error) { console.warn("season_rankings load error:", error); return []; }
  return (data || []).map((r) => ({
    playerId: r.player_id,
    name: r.player_name,
    position: r.position,
    initialPosition: r.initial_position,
  }));
}

/** Resultados de uma temporada específica (usado na aba Temporadas). */
export async function dbLoadResultsBySeason(season, gender = "M") {
  const { data, error } = await supabase
    .from(t(gender, "results"))
    .select("*")
    .eq("season", season)
    .order("date", { ascending: true });
  if (error) { console.warn("results by season load error:", error); return []; }
  return (data || []).map(mapResult);
}

// ─── PENALIDADE DE W.O. (transação no banco) ──────────────────────────────────

/**
 * Aplica um W.O. em UMA transação no banco (função SQL apply_wo_penalty):
 * trava o desafio, recalcula as posições com os dados atuais e grava posições,
 * resultado, histórico, notificações e remoção do desafio — tudo ou nada.
 *
 * type:   "wo-challenged" (desafiado não compareceu/recusou/expirou)
 *         | "wo-challenger" (desafiante não compareceu — só via admin)
 * origin: "recusa" | "expiracao" | "admin"
 * Retorna { applied, reason?, penalized_id?, from?, to?, drop? }.
 * applied=false com reason "already_processed" = outra sessão já tratou o desafio.
 */
export async function dbApplyWo({ gender, challengeId, type, origin }) {
  const { data, error } = await supabase.rpc("apply_wo_penalty", {
    p_gender: gender,
    p_challenge_id: challengeId,
    p_type: type,
    p_origin: origin,
    p_season: CURRENT_SEASON,
  });
  if (error) throw error;
  return data;
}
